import { ApiError } from '@pn/core/errors';
import { apiWellLicenseeHistoryProvider } from '@pn/services/api/intel/apiWellLicenseeHistoryProvider';
import type { ApiLicenseeHistoryRecord } from '@pn/services/api/intel/types';
import { isNil } from 'lodash-es';
import React from 'react';

async function fetchWellLicenseeHistory(
  input: string
): Promise<ApiLicenseeHistoryRecord[]> {
  const cleanedInput = input.trim().replace(/[^a-zA-Z0-9]/g, '');
  const res =
    await apiWellLicenseeHistoryProvider.getWellLicenseeHistory(cleanedInput);

  /**
   * HACK: remove records that show no change in licensee. Combine the duplicates into one entry with the proper start and end dates adjusted
   * This is a DB issue, but to get this out, we are ok with doing this on the frontend for now
   */
  return res.reduce<ApiLicenseeHistoryRecord[]>((acc, record) => {
    const prev = acc[acc.length - 1];
    if (
      prev &&
      !isNil(record.company_brand_id) &&
      prev.company_brand_id === record.company_brand_id
    ) {
      // extend the existing entry's start date (records are newest-first)
      acc[acc.length - 1] = { ...prev, start_date: record.start_date };
    } else {
      acc.push({ ...record });
    }
    return acc;
  }, []);
}

export function useWellLicenseeHistory() {
  const [history, setHistory] = React.useState<
    ApiLicenseeHistoryRecord[] | undefined
  >(undefined);
  const [errorMsg, setErrorMsg] = React.useState('');
  const [isFetching, setIsFetching] = React.useState(false);

  const fetch = React.useCallback(async (input: string) => {
    setHistory(undefined);
    setErrorMsg('');
    setIsFetching(true);
    try {
      const filtered = await fetchWellLicenseeHistory(input);
      setHistory(filtered);
    } catch (error) {
      setHistory([]);
      if (error instanceof ApiError) {
        if (error.code === 404) {
          setErrorMsg('No history found for this UWI or Licence Number.');
        } else if (error.code === 403) {
          setErrorMsg('You do not have access to view this data.');
        } else {
          setErrorMsg('Failed to fetch data from the server.');
          console.error('Error fetching well licence history:', error);
        }
      } else {
        setErrorMsg('An unexpected error occurred.');
        console.error('Error fetching well licence history:', error);
      }
    } finally {
      setTimeout(() => setIsFetching(false), 600); // API is so fast, that we want to delay it a bit to prevent spamming
    }
  }, []);

  const clear = React.useCallback(() => {
    setHistory(undefined);
    setErrorMsg('');
  }, []);

  return { history, errorMsg, isFetching, fetch, clear };
}
